/**
 * VoiceDeskController — the app-shell side of the Voice Agent Desk (module
 * voice_desk). Mounted once next to InboxWaitingNotifier.
 *
 *  - feeds the call store (services/voiceDesk.js) from the agents' stream
 *    (relayed by InboxWaitingNotifier as window "xenbox:agent-event")
 *  - rings while a call waits for an agent
 *  - a call handed over by the AI opens the Inbox on that conversation, where
 *    VoiceCallCard shows it as a new interaction to answer
 *  - during a call on any other page, a small bar leads back to it
 *
 * A call is a high-priority live interaction: from any page the agent is taken
 * to the Inbox on it; inside the Inbox chats it opens over the current chat.
 * Only an agent already on a call is left where they are.
 */
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { LuHeadset, LuPhoneOff } from 'react-icons/lu';

import { useAuth } from '../provider/AuthProvider';
import { useInstalledApps } from '../provider/InstalledAppsProvider';
import useTranslation from '../hooks/useTranslation';
import Button from './ui/Button';
import { showNotification } from '../pages/components/Notification';
import {
  configureVoiceDesk, hangUp, leaveDesk, onAgentEvent, refresh, ringingHandoffs, useVoiceDesk, voiceDeskState,
} from '../services/voiceDesk';
import { elapsed } from '../pages/inbox/components/VoiceCallCard';

const DESK_GROUPS = ['Inbox / Agent', 'Inbox / Manager', 'Administration / Settings'];
const RING_EVERY_MS = 4000;
export const INBOX_CHATS = '/inbox/chats';

const ring = () => {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    [0, 0.45].forEach((offset) => {
      [[660, 0], [880, 0.14]].forEach(([freq, at]) => {
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.connect(g);
        g.connect(ctx.destination);
        o.frequency.value = freq;
        const t = ctx.currentTime + offset + at;
        g.gain.setValueAtTime(0.0001, t);
        g.gain.exponentialRampToValueAtTime(0.16, t + 0.02);
        g.gain.exponentialRampToValueAtTime(0.0001, t + 0.13);
        o.start(t);
        o.stop(t + 0.15);
      });
    });
    setTimeout(() => { try { ctx.close(); } catch { /* noop */ } }, 1200);
  } catch { /* noop */ }
};

const VoiceDeskController = () => {
  const { userdetails } = useAuth();
  const { isAppInstalled } = useInstalledApps();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const desk = useVoiceDesk();

  const groups = userdetails?.group_names || [];
  const enabled = isAppInstalled('voice_desk') && Boolean(userdetails?.is_superuser || groups.some((g) => DESK_GROUPS.includes(g)));
  const inChats = location.pathname.startsWith(INBOX_CHATS);

  useEffect(() => {
    configureVoiceDesk({ onNotify: (key, type) => showNotification(t(key), type) });
  }, [t]);

  // Latest location for the event handler, without resubscribing on every navigation.
  const inChatsRef = React.useRef(inChats);
  inChatsRef.current = inChats;

  useEffect(() => {
    if (!enabled) {
      leaveDesk();
      return undefined;
    }
    refresh();
    const onEvent = (e) => {
      const data = e.detail || {};
      if (data.type === 'connected') {
        refresh();
        return;
      }
      const arrived = onAgentEvent(data);
      // A new call opens the Inbox on it (inside the chats, the Inbox opens it itself), unless the agent is on a call.
      if (arrived && !inChatsRef.current && !voiceDeskState().call && arrived.conversation_id) {
        navigate(`${INBOX_CHATS}?conversation=${arrived.conversation_id}`);
      }
    };
    window.addEventListener('xenbox:agent-event', onEvent);
    return () => window.removeEventListener('xenbox:agent-event', onEvent);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  const ringing = enabled && ringingHandoffs(desk).length > 0;
  useEffect(() => {
    if (!ringing) return undefined;
    ring();
    const id = setInterval(ring, RING_EVERY_MS);
    return () => clearInterval(id);
  }, [ringing]);

  const onCall = enabled && Boolean(desk.call);
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!onCall) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [onCall]);

  useEffect(() => {
    if (!onCall) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [onCall]);

  if (!onCall || inChats) return null;

  const { handoff } = desk.call;
  const caller = handoff.call?.caller || handoff.call?.from_number || t('voice.desk_unknown_caller');
  return (
    <div className="fixed bottom-4 end-4 start-4 sm:start-auto z-[var(--cx-z-toast)] flex items-center gap-3 bg-surface border border-success rounded-[var(--cx-radius)] shadow-chart-hover ps-3 pe-2 py-2"
         role="region" aria-label={t('voice.desk_phone')}>
      <LuHeadset className="text-success shrink-0" aria-hidden />
      <div className="min-w-0 text-sm">
        <div className="font-semibold text-ink truncate">{caller}</div>
        <div className="text-xs text-text-2 tabular-nums" dir="ltr">{elapsed(handoff.accepted_at, now)}</div>
      </div>
      <Button size="sm" variant="secondary" to={`${INBOX_CHATS}?conversation=${handoff.conversation_id}`}>{t('voice.desk_back_to_call')}</Button>
      <Button size="sm" variant="danger" iconOnly icon={<LuPhoneOff />} aria-label={t('voice.hang_up')} title={t('voice.hang_up')} onClick={() => hangUp()} />
    </div>
  );
};

export default VoiceDeskController;
